import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter, Card, Button } from '../ui';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface PersonalItem {
  id: string;
  category: 'documents' | 'physical_assets' | 'collectibles' | 'electronics' | 'digital_assets' | 'custom';
  type?: 'account' | 'jewelry' | 'artwork' | 'collectible' | 'document' | 'other';
  name: string;
  description?: string;
  estimatedValue?: number;
  location?: string;
  recipient?: string;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame802: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const categories = [
    { key: 'documents' as const, label: 'Important Documents', color: 'text-stone-600' },
    { key: 'physical_assets' as const, label: 'Physical Assets', color: 'text-blue-600' },
    { key: 'collectibles' as const, label: 'Collectibles & Valuables', color: 'text-purple-600' },
    { key: 'electronics' as const, label: 'Electronics', color: 'text-cyan-600' },
    { key: 'digital_assets' as const, label: 'Digital Assets', color: 'text-green-600' },
    { key: 'custom' as const, label: 'Other Assets', color: 'text-gray-600' },
  ];

  const migrateItem = (item: PersonalItem): PersonalItem => {
    const typeToCategory: Record<string, PersonalItem['category']> = {
      document: 'documents',
      jewelry: 'collectibles',
      artwork: 'collectibles',
      collectible: 'collectibles',
      account: 'custom',
      other: 'custom',
    };

    const category = item.category || (item.type ? typeToCategory[item.type] || 'custom' : 'custom');

    return {
      ...item,
      category,
      documents: normalizeDocuments(item.documents || []),
    };
  };

  const personalItems: PersonalItem[] =
    (session?.taskProgress?.personal?.answers?.personalItems ?? []).map(migrateItem);

  useEffect(() => {
    const sessionItems = session?.taskProgress?.personal?.answers?.personalItems;
    if (sessionItems && sessionItems.length > 0) return;

    try {
      const backupData = localStorage.getItem('personal-items-backup');
      if (!backupData) return;

      const parsed = JSON.parse(backupData);
      if (!Array.isArray(parsed) || parsed.length === 0) return;

      const migrated = parsed.map(migrateItem);
      updateTaskProgress('personal', { personalItems: migrated }, 'in-progress');
    } catch (error) {
      console.error('[Frame802] Failed to restore from backup:', error);
    }
  }, [session?.taskProgress?.personal?.answers?.personalItems, updateTaskProgress]);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame802] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleEdit = () => navigate('/frame-801');

  const handleRemoveAttachedDocument = async (itemId: string, documentIndex: number) => {
    const item = personalItems.find((entry) => entry.id === itemId);
    const docToRemove = item?.documents?.[documentIndex];

    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame802] Failed to delete document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedItems = personalItems.map((entry) => {
      if (entry.id !== itemId) return entry;
      return {
        ...entry,
        documents: normalizeDocuments(entry.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    try {
      localStorage.setItem('personal-items-backup', JSON.stringify(updatedItems));
      localStorage.setItem(
        'personal-items-data-backup',
        JSON.stringify({
          personalItems: updatedItems,
          lastUpdated: new Date().toISOString(),
          taskId: 'personal',
        })
      );
    } catch (error) {
      console.error('[Frame802] Failed to save personal items backup:', error);
    }

    await updateTaskProgress('personal', { personalItems: updatedItems }, 'in-progress');
  };

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const itemsByCategory = personalItems.reduce((acc, item) => {
    const category = item.category || 'custom';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {} as Record<string, PersonalItem[]>);

  const totalValue = personalItems.reduce((sum, item) => sum + (item.estimatedValue || 0), 0);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title={`Your personal accounts and belongings, ${session.name || 'there'}!`} />

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Personal Items Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">{personalItems.length}</div>
                <div className="text-sm text-stone-600">Total Items</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{formatCurrency(totalValue)}</div>
                <div className="text-sm text-stone-600">Total Value</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Personal Items by Category</h3>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                Edit
              </Button>
            </div>

            {personalItems.length > 0 ? (
              <div className={personalItems.length >= 3 ? 'max-h-[400px] overflow-y-auto pr-2 custom-scrollbar' : ''}>
                <div className="space-y-6">
                  {categories.map((category) => {
                    const categoryItems = itemsByCategory[category.key];
                    if (!categoryItems || categoryItems.length === 0) return null;

                    const categoryTotal = categoryItems.reduce((sum, item) => sum + (item.estimatedValue || 0), 0);

                    return (
                      <div key={category.key}>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-lg font-semibold text-stone-800">{category.label}</h3>
                          <span className="text-sm text-stone-500">({categoryItems.length} items)</span>
                          {categoryTotal > 0 && (
                            <div className="ml-auto text-base font-medium text-green-600">
                              {formatCurrency(categoryTotal)}
                            </div>
                          )}
                        </div>

                        <div className="space-y-3">
                          {categoryItems.map((item, index) => {
                            const attachedDocs = normalizeDocuments(item.documents || []);

                            return (
                              <div key={item.id || index} className="bg-stone-50 rounded-lg p-4">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-stone-800 break-words">{item.name}</h4>

                                  {item.description && (
                                    <p className="text-sm text-stone-600 mt-1 break-words">{item.description}</p>
                                  )}

                                  <div className="flex flex-col gap-1 mt-2 text-sm sm:flex-row sm:flex-wrap sm:gap-4">
                                    {item.estimatedValue !== undefined && (
                                      <span className="text-green-600 font-medium break-words">
                                        Value: {formatCurrency(item.estimatedValue)}
                                      </span>
                                    )}
                                    {item.location && (
                                      <span className="text-stone-500 break-words">Location: {item.location}</span>
                                    )}
                                    {item.recipient && (
                                      <span className="text-cyan-600 break-words">Recipient: {item.recipient}</span>
                                    )}
                                  </div>

                                  {attachedDocs.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                      {attachedDocs.map((doc, docIndex) => (
                                        <div
                                          key={`${item.id}-${doc.id}-${docIndex}`}
                                          className="flex flex-col gap-3 rounded-md border border-stone-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => openDocument(doc)}
                                            className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                          >
                                            <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                            <span
                                              className="text-sm font-medium text-stone-800 truncate min-w-0"
                                              title={doc.name}
                                            >
                                              {doc.name}
                                            </span>
                                          </button>

                                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                            <button
                                              type="button"
                                              onClick={() => downloadDocument(doc)}
                                              className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                            >
                                              Download
                                            </button>

                                            <button
                                              type="button"
                                              onClick={() => handleRemoveAttachedDocument(item.id, docIndex)}
                                              className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                              title="Remove document"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {item.notes && (
                                    <p className="text-sm text-stone-500 mt-2 italic break-words">{item.notes}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No personal items added yet.</p>
                <Button variant="outline" onClick={handleEdit} className="mt-2">
                  Add Personal Items
                </Button>
              </div>
            )}
          </Card>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Organize Personal Accounts and Belongings"
        subtitle="Review and manage your personal inventory"
        progressPercent={100}
        onBack={() => navigate('/frame-801')}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="personal"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame802;